import {
  Button,
  FileButton,
  NumberInput,
  Paper,
  Select,
  Stack,
  Textarea,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useUploadYieldMap from '../../data-access/hooks/use-upload-yield-map/upload-yield-map';

import styles from './upload-yield-map-form.module.css';

import { useSharedQueries } from '@data-access';

interface FormValues {
  file: File | null;
  farm: string;
  yield_float: number;
  comment: string;
}

export function UploadYieldMapForm() {
  const { t } = useTranslation();
  const uploadYieldMap = useUploadYieldMap();
  const form = useForm<FormValues>({
    initialValues: {
      file: null,
      farm: '',
      yield_float: 0,
      comment: '',
    },
  });

  const sharedQueries = useSharedQueries();
  const farms = useQuery(sharedQueries.farms());

  const handleSubmit = (values: FormValues) => {
    const { file, farm, yield_float, comment } = values;
    if (!file) {
      return;
    }

    uploadYieldMap.mutate(
      {
        file,
        farm: parseInt(farm),
        yield_float,
        comment,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: t('common.success'),
            message: t('uploadYieldMapForm.successMessage'),
            color: 'green',
          });
        },
      },
    );
  };

  useEffect(() => {
    if (uploadYieldMap.isSuccess) {
      form.reset();
      uploadYieldMap.reset();
    }
  }, [form, uploadYieldMap, uploadYieldMap.isSuccess]);

  return (
    <form className={clsx(styles.form)} onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Title mb="sm" order={2}>
          {t('uploadYieldMapForm.title')}
        </Title>

        <FileButton
          accept=".zip,.rar,.7z,.gz"
          onChange={form.getInputProps('file').onChange}
        >
          {(props) => (
            <Button {...props} maw={250} variant="outline">
              {t('uploadYieldMapForm.selectFile')}
            </Button>
          )}
        </FileButton>
        {form.values.file ? <div>{form.values.file.name}</div> : null}

        <Select
          label={t('uploadYieldMapForm.farm')}
          required
          {...form.getInputProps('farm')}
          data={farms.data}
          searchable
        />

        <NumberInput
          label={t('uploadYieldMapForm.yieldLabel')}
          {...form.getInputProps('yield_float')}
          decimalScale={2}
          hideControls
        />

        <Textarea
          label={t('uploadYieldMapForm.comment')}
          {...form.getInputProps('comment')}
          autosize
          maxLength={200}
          maxRows={5}
          minRows={3}
        />
      </Stack>

      <Button
        loading={uploadYieldMap.isPending}
        type="submit"
        variant="primary"
      >
        {t('uploadYieldMapForm.submit')}
      </Button>
    </form>
  );
}

export default UploadYieldMapForm;
